import { Box, Typography } from '@mui/material';
import { Linkable } from '@/components/blocks/Linkable';
import {
	usePersonalizationExpiration,
	usePersonalizationPercentage,
	usePersonalizationSizes,
} from '@/data/Content/RepurchaseCoupon';
import { usePersonalGender } from '@/data/Content/PersonalGender';
import {
	GenderHrefs,
	resolveCtaHref,
	toFootwearSize,
} from '@/components/blocks/DpRepurchaseCoupon/buildFacetHref';
import {
	usePersonalizationPurchase,
	usePersonalizationTokens,
} from '@/components/blocks/DpRepurchaseCoupon/usePersonalizationTokens';
import { formatCouponDate } from '@/components/blocks/DpRepurchaseCoupon/formatCouponDate';
import { fillPersonalization } from '@/components/blocks/DpRepurchaseCoupon/fillPersonalization';
import { HighlightedText } from '@/components/blocks/HighlightedText';
import {
	repurchaseCtaButtonSX,
	repurchaseCtaInnerSX,
	repurchaseCtaRootSX,
	repurchaseCtaTextSX,
	repurchaseCtaTitleSX,
	repurchaseCtaValidityStrongSX,
	repurchaseCtaValiditySX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/cta';

export type RepurchaseCtaContent = {
	title?: string;
	text?: string;
	buttonLabel?: string;
	noSizeButtonLabel?: string;
	validityText?: string;
	sizeFacet?: string;
	genderHrefs?: GenderHrefs;
};

const TITLE_ID = 'dp-repurchase-cta-title';
const PERCENTAGE_TOKEN = '{porcentaje}';
const DEFAULT_TITLE = '¿Aún no encuentras lo tuyo?';
const DEFAULT_TEXT = `Tu ${PERCENTAGE_TOKEN} aplica en todo el sitio. Explora más en tu talla antes de que se vaya.`;
const DEFAULT_BUTTON_LABEL = 'Ver productos en mi talla';
const DEFAULT_NO_SIZE_BUTTON_LABEL = 'Explorar productos';
const DEFAULT_VALIDITY_TEXT = '[Vence el {fecha}] · Un solo uso';

export const RepurchaseCta = ({
	title = DEFAULT_TITLE,
	text = DEFAULT_TEXT,
	buttonLabel = DEFAULT_BUTTON_LABEL,
	noSizeButtonLabel = DEFAULT_NO_SIZE_BUTTON_LABEL,
	validityText = DEFAULT_VALIDITY_TEXT,
	sizeFacet,
	genderHrefs,
}: RepurchaseCtaContent) => {
	const percentage = usePersonalizationPercentage();
	const expirationDateTime = usePersonalizationExpiration();
	const { sizes } = usePersonalizationSizes();
	const profileGender = usePersonalGender();
	const purchase = usePersonalizationPurchase();
	const tokens = usePersonalizationTokens();
	const size = sizes?.footwear ?? purchase.size;
	const href = resolveCtaHref({
		genderHrefs,
		gender: profileGender ?? purchase.gender,
		size,
		sizeFacet,
	});
	const withSize = !!sizeFacet && !!toFootwearSize(size);

	if (!percentage) {
		return null;
	}

	const withPercentage = (value: string) => value.replaceAll(PERCENTAGE_TOKEN, `${percentage}%`);
	const validity = fillPersonalization(validityText, {
		...tokens,
		fecha: expirationDateTime ? formatCouponDate(expirationDateTime) : undefined,
	});

	return (
		<Box component="section" aria-labelledby={TITLE_ID} sx={repurchaseCtaRootSX}>
			<Box sx={repurchaseCtaInnerSX}>
				<Typography id={TITLE_ID} component="h2" sx={repurchaseCtaTitleSX}>
					{withPercentage(title)}
				</Typography>
				<Typography component="p" sx={repurchaseCtaTextSX}>
					{withPercentage(text)}
				</Typography>
				{href ? (
					<Linkable href={href} sx={repurchaseCtaButtonSX}>
						{withPercentage(withSize ? buttonLabel : noSizeButtonLabel)}
					</Linkable>
				) : null}
				{validity ? (
					<Typography component="p" sx={repurchaseCtaValiditySX}>
						<HighlightedText text={validity} sx={repurchaseCtaValidityStrongSX} />
					</Typography>
				) : null}
			</Box>
		</Box>
	);
};
